import type { Microsoft365Feature, Microsoft365StatusDto } from '@dbrain/shared';
import { Badge, Button, Icons } from '@dbrain/ui';
import { useTranslation } from 'react-i18next';
import { isApiError } from '../../api/client.js';
import { useMicrosoft365ConsentRequest } from '../../api/microsoft365.js';
import { ErrorAlert } from '../ErrorAlert.js';
import { MicrosoftIcon } from './MicrosoftIcon.js';
import { toolsOf, writeToolsOf } from './state.js';

/** The products in the order the mockups name them (MsBaglantilar); OneDrive and SharePoint only while the firm has them on. */
const PRODUCTS: { feature: Microsoft365Feature; key: 'mail' | 'calendar' | 'onedrive' | 'sharepoint'; always: boolean }[] = [
  { feature: 'mail_read', key: 'mail', always: true },
  { feature: 'calendar_read', key: 'calendar', always: true },
  { feature: 'onedrive_import', key: 'onedrive', always: false },
  { feature: 'sharepoint_import', key: 'sharepoint', always: false },
];

/**
 * The product lineup ("Outlook mail", "Outlook takvim", and OneDrive and SharePoint while their switches are on):
 * Microsoft's own icon right before each product's name, a tick once the person is connected, "kapalı" for what the
 * firm turned off.
 */
export function Microsoft365Products({ features, connected, size = 20 }: { features: Record<Microsoft365Feature, boolean>; connected: boolean; size?: 16 | 20 }) {
  const { t } = useTranslation();
  return (
    <ul className="m365-products" aria-label={t('microsoft365.user.products.label')} data-testid="microsoft365-products">
      {PRODUCTS.filter((product) => product.always || features[product.feature]).map(({ feature, key }) => {
        const on = features[feature];
        return (
          <li key={key} className={on ? 'm365-product' : 'm365-product m365-product--off'} data-testid="microsoft365-product" data-feature={feature} data-on={on ? 'true' : 'false'}>
            <MicrosoftIcon product={key === 'mail' || key === 'calendar' ? 'outlook' : key} size={size} />
            <span className="m365-product__name">{t(`microsoft365.user.products.${key}`)}</span>
            {!on ? (
              <span className="m365-product__off">{t('microsoft365.user.products.off')}</span>
            ) : connected ? (
              <>
                <Icons.check size={15} className="m365-product__check" />
                <span className="ui-visually-hidden">{t('microsoft365.user.products.ready')}</span>
              </>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * "Ayrıntılar": the six read tools in plain words, each "Salt okunur" (or off when the firm turned its part off);
 * part 3's write tools of the switches that are on, each "Her seferinde onayınızla" or "İzin gerekiyor" while the
 * account has not granted the permission yet (no mockup).
 */
export function Microsoft365Tools({ status }: { status: Pick<Microsoft365StatusDto, 'features' | 'grants'> }) {
  const { t } = useTranslation();
  const writes = writeToolsOf(status);
  return (
    <>
      <p className="m365-tools__note">{t('microsoft365.user.tools.note')}</p>
      <ul className="connector-tools m365-tools" data-testid="microsoft365-tools">
        {toolsOf(status.features).map(({ tool, on }) => (
          <li key={tool} className="m365-tools__item" data-tool={tool} data-on={on ? 'true' : 'false'}>
            <span className="m365-tools__name">{t(`microsoft365.user.tools.${tool}.title`)}</span>
            <Badge tone={on ? 'neutral' : 'warning'}>{on ? t('microsoft365.user.tools.readOnly') : t('microsoft365.user.tools.off')}</Badge>
            <span className="m365-tools__text">{t(`microsoft365.user.tools.${tool}.text`)}</span>
          </li>
        ))}
      </ul>
      {writes.length > 0 ? (
        <>
          <p className="m365-tools__note">{t('microsoft365.user.tools.writeNote')}</p>
          <ul className="connector-tools m365-tools" data-testid="microsoft365-write-tools">
            {writes.map(({ tool, granted }) => (
              <li key={tool} className="m365-tools__item" data-tool={tool} data-granted={granted ? 'true' : 'false'}>
                <span className="m365-tools__name">{t(`microsoft365.user.tools.${tool}.title`)}</span>
                <Badge tone={granted ? 'neutral' : 'warning'}>{granted ? t('microsoft365.user.tools.approval') : t('microsoft365.user.tools.grant')}</Badge>
                <span className="m365-tools__text">{t(`microsoft365.user.tools.${tool}.text`)}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  );
}

/**
 * "Yöneticiye bildir": tells the firm's admins that Microsoft asked for their consent. Sent (or already sent
 * within the hour, which the server refuses to repeat): a quiet line instead of the button.
 */
export function ConsentRequestButton({ variant = 'secondary' }: { variant?: 'primary' | 'secondary' }) {
  const { t } = useTranslation();
  const request = useMicrosoft365ConsentRequest();
  const already = isApiError(request.error, 'rate_limited');
  if (request.isSuccess || already) {
    return (
      <p className="m365-sent" role="status" data-testid="microsoft365-consent-sent">
        <Icons.check size={15} />
        {already ? t('microsoft365.user.consent.already') : t('microsoft365.user.consent.sent')}
      </p>
    );
  }
  return (
    <div className="m365-consent">
      <Button size="sm" variant={variant} busy={request.isPending} onClick={() => request.mutate()} data-testid="microsoft365-consent-request">
        {t('microsoft365.user.consent.request')}
      </Button>
      <ErrorAlert error={request.error} />
    </div>
  );
}
